import { useEffect, useState } from "react";
import backendApi from "../api/backendApi";
import { useNavigate } from "react-router-dom";
import AlertModal from "../components/modal/AlertModal";
import LoadingButton from "../components/LoadingButton";

const RegisterForm = ({ onSwitch }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    aimsNo: "",
    muqami: "",
    tanzeem: "",
  });

  const [muqamiList, setMuqamiList] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [title, setTitle] = useState("");

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchMuqamis = async () => {
      try {
        const res = await backendApi.get("/muqamis");
        setMuqamiList(res.data);
      } catch (err) {
        console.error("Failed to fetch muqamis:", err);
      }
    };
    fetchMuqamis();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await backendApi.post("/auth/register", formData);
      navigate("/email-sent", { state: { email: formData.email } });
    } catch (err) {
      const apiError = err?.response?.data;
      const httpStatus = err?.response?.status;

      let title = "Register unsuccessful";
      let body = "Please try again later.";

      if (!apiError) {
        // Network error / CORS / server down
        title = "Network error";
        body =
          "Unable to reach server. Please check your connection and try again.";
      } else {
        const { code, message, errors } = apiError;

        // 1) Duplicate email
        if (
          httpStatus === 409 ||
          code === "EMAIL_ALREADY_REGISTERED" ||
          code === "DUPLICATE_EMAIL"
        ) {
          title = "Email already registered";
          body = "Please use another email.";
        }
        // 2) Validation issues with field-level details
        else if (
          code === "VALIDATION_FAILED" ||
          code === "BINDING_FAILED" ||
          code === "CONSTRAINT_VIOLATION" ||
          Array.isArray(errors)
        ) {
          title = "Please fix the highlighted fields";
          if (Array.isArray(errors) && errors.length > 0) {
            // Build a readable list from field errors
            body = errors
              .map((e) => `${e.field ?? "field"}: ${e.message ?? "invalid"}`)
              .join("\n");
          } else if (message) {
            body = message;
          } else {
            body = "One or more fields are invalid.";
          }
        }
        // 3) Any other known error with a meaningful message
        else if (typeof message === "string" && message.trim().length > 0) {
          title = "Register unsuccessful";
          body = message;
        }
      }

      setTitle(title);
      setModalMessage(body);
      setIsSuccess(false);
      setModalOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold">Create an Account</h2>
          <p className="text-gray-600">
            Please fill in your details below to register
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
          <input
            name="firstName"
            onChange={handleChange}
            placeholder="First Name"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring"
            required
          />
          <input
            name="lastName"
            onChange={handleChange}
            placeholder="Last Name"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring"
            required
          />
          <input
            name="aimsNo"
            onChange={handleChange}
            placeholder="AIMS No"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring"
            required
          />
          <input
            name="email"
            type="email"
            onChange={handleChange}
            placeholder="Email"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring"
            required
          />
          <input
            name="phone"
            onChange={handleChange}
            placeholder="Phone"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring"
            required
          />

          <select
            name="muqami"
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring"
            required
          >
            <option value="">Select Muqami</option>
            {muqamiList.map((m) => (
              <option key={m} value={m}>
                {m
                  .toLowerCase()
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")}
              </option>
            ))}
          </select>

          <ul className="grid w-full gap-3 grid-cols-2">
            <li>
              <input
                type="radio"
                id="khuddam-radio"
                name="tanzeem"
                value="KHUDDAM"
                className="hidden peer"
                required
                onChange={handleChange}
              />
              <label
                htmlFor="khuddam-radio"
                className="inline-flex items-center justify-center w-full p-3 text-black bg-white border border-gray-200 rounded-lg cursor-pointer peer-checked:text-slate-200 peer-checked:bg-slate-950 peer-checked:font-medium hover:text-gray-600 hover:bg-gray-100"
              >
                Khuddam
              </label>
            </li>
            <li>
              <input
                type="radio"
                id="athfal-radio"
                name="tanzeem"
                value="ATHFAL"
                className="hidden peer"
                onChange={handleChange}
              />
              <label
                htmlFor="athfal-radio"
                className="inline-flex items-center justify-center w-full p-3 text-black bg-white border border-gray-200 rounded-lg cursor-pointer peer-checked:text-slate-200 peer-checked:bg-slate-950 peer-checked:font-medium hover:text-gray-600 hover:bg-gray-100"
              >
                Athfal
              </label>
            </li>
          </ul>

          <LoadingButton loading={submitting} type="submit" variant="dark">
            Register
          </LoadingButton>

          <p className="text-center mt-4">
            Already have an account?{" "}
            <span
              onClick={onSwitch}
              className="underline text-indigo-800 cursor-pointer"
            >
              Sign In
            </span>
          </p>
        </form>
      </div>

      <AlertModal
        isOpen={modalOpen}
        title={title}
        message={modalMessage}
        isOneButton={true}
        isSuccess={isSuccess}
        onSuccessOneButton={() => setModalOpen(false)}
      />
    </div>
  );
};

export default RegisterForm;
